import React, { useState, useEffect } from 'react';
import { supabase } from '../auth';

// Icons using SVG directly to avoid dependency issues if lucide-react isn't available or configured
const Icons = {
    Home: () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-500"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
    ),
    UserPlus: () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></svg>
    ),
    UserMinus: () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="23" y1="11" x2="17" y2="11" /></svg>
    ),
    Copy: () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
    ),
    RefreshCw: () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin text-gray-400"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></svg>
    ),
    X: () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 18 12" /></svg>
    )
};

export default function HouseholdSection({ profile, membership, householdTitle = 'Household' }) {
    const [isLoading, setIsLoading] = useState(false);
    const [members, setMembers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [showInviteDialog, setShowInviteDialog] = useState(false);
    const [showRemoveDialog, setShowRemoveDialog] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [inviteUrl, setInviteUrl] = useState('');
    const [copyText, setCopyText] = useState('Copy Link');

    const loadHouseholdData = async () => {
        if (!profile || !membership) return;
        if (!profile.household_id) return;

        setIsLoading(true);
        try {
            // Load members
            const { data: membersData, error: mErr } = await supabase.functions.invoke('household-ops', {
                method: 'POST',
                body: { action: 'get_members' }
            });

            if (mErr) throw mErr;
            if (membersData.success) {
                setMembers(membersData.members);
            }

            // Load invites
            const { data: invitesData, error: iErr } = await supabase.functions.invoke('household-ops', {
                method: 'POST',
                body: { action: 'get_invites' }
            });

            if (iErr) throw iErr;
            if (invitesData.success) {
                setInvites(invitesData.invites);
            }

        } catch (error) {
            console.error('Error loading household data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadHouseholdData();
    }, [profile?.household_id]);

    const handleCreateInvite = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase.functions.invoke('household-ops', {
                method: 'POST',
                body: { action: 'create_invite' }
            });

            if (error) throw error;

            if (data.success) {
                setInviteUrl(data.inviteUrl);
                setShowInviteDialog(true);
                loadHouseholdData(); // Refresh invites
            } else {
                alert(data.error || 'Failed to create invite');
            }
        } catch (error) {
            console.error('Error creating invite:', error);
            alert('Failed to create invite');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyInvite = () => {
        navigator.clipboard.writeText(inviteUrl);
        setCopyText('Copied!');
        setTimeout(() => setCopyText('Copy Link'), 2000);
    };

    const handleRemoveMember = (member) => {
        setSelectedMember(member);
        setShowRemoveDialog(true);
    };

    const confirmRemoveMember = async () => {
        if (!selectedMember) return;

        setIsLoading(true);
        try {
            const { data, error } = await supabase.functions.invoke('household-ops', {
                method: 'POST',
                body: { action: 'remove_member', profileId: selectedMember.id }
            });

            if (error) throw error;

            if (data.success) {
                loadHouseholdData();
            } else {
                alert(data.error || 'Failed to remove member');
            }
        } catch (error) {
            console.error('Error removing member:', error);
            alert('Failed to remove member');
        } finally {
            setIsLoading(false);
            setShowRemoveDialog(false);
        }
    };

    const membersLimit = membership?.max_members || 2;
    const isHouseholdFull = (members.length + invites.length) >= membersLimit;
    const canInvite = profile?.is_household_admin && membership?.activated_at;
    const hasActiveInvite = invites.length > 0;

    if (!profile.household_id) {
        return <div className="text-gray-600">No household set up yet.</div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-medium">{householdTitle}</h3>
            </div>

            {isLoading && members.length === 0 ? (
                <div className="flex justify-center py-4"><Icons.RefreshCw /></div>
            ) : (
                <div className="space-y-3">
                    {members.map(member => (
                        <div key={member.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                            <div>
                                <div className="font-medium flex items-center gap-1">
                                    {member.name}
                                    {member.isCurrentUser && <span className="text-xs text-gray-500">(You)</span>}
                                    {member.is_household_admin && <span className="ml-2 text-xs bg-brand-100 text-brand-800 px-2 py-1 rounded">Admin</span>}
                                </div>
                                <div className="text-sm text-gray-500">{member.pronouns}</div>
                            </div>
                            {profile.is_household_admin && !member.isCurrentUser && (
                                <button
                                    onClick={() => handleRemoveMember(member)}
                                    className="text-red-500 hover:text-red-600 text-sm flex items-center gap-1 px-3 py-1 border border-red-200 rounded hover:bg-red-50 transition-colors"
                                >
                                    <Icons.UserMinus /> Remove
                                </button>
                            )}
                        </div>
                    ))}

                    {/* Render Empty Slots */}
                    {[...Array(Math.max(0, membersLimit - members.length - invites.length))].map((_, i) => (
                        <div key={`empty-${i}`} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0 border-dashed">
                            <div className="text-gray-400 italic flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center">
                                    <Icons.Home className="text-gray-300 w-4 h-4" />
                                </div>
                                <span>Empty Slot</span>
                            </div>
                            {canInvite && (
                                <button onClick={handleCreateInvite} className="text-sm text-brand-700 hover:text-brand-800 font-medium px-3 py-1 bg-brand-50 rounded hover:bg-brand-100 transition-colors">
                                    Invite
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {invites.length > 0 && (
                <div className="mt-6">
                    <h4 className="text-sm font-medium mb-3 text-gray-700">Active Invites</h4>
                    <div className="space-y-2">
                        {invites.map(invite => (
                            <div key={invite.id} className="flex justify-between items-center py-2 border-t border-gray-100">
                                <div>
                                    <div className="text-sm text-gray-700">Created {new Date(invite.created_at).toLocaleDateString()}</div>
                                    <div className="text-xs text-gray-500 font-mono">{invite.code}</div>
                                </div>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(invite.inviteUrl);
                                        alert('Invite link copied!');
                                    }}
                                    className="text-brand-700 hover:text-brand-800 text-sm flex items-center gap-1 px-3 py-1 border border-brand-200 rounded hover:bg-brand-50 transition-colors"
                                >
                                    <Icons.Copy /> Copy Link
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Main Invite Button - Only show if not full and no empty slots rendered (redundant if using per-slot button, but maybe keep as primary CTA?) 
                Actually, let's keep it as a primary CTA at the bottom if there are slots content.
            */}
            {canInvite && !isHouseholdFull && members.length > 0 && (
                <button
                    onClick={handleCreateInvite}
                    disabled={isLoading}
                    className="btn btn-primary w-full mt-4 flex items-center justify-center gap-2"
                >
                    <Icons.UserPlus />
                    {hasActiveInvite ? 'Create New Invite' : 'Invite to Household'}
                </button>
            )}

            {canInvite && isHouseholdFull && (
                <div className="text-sm text-gray-500 mt-4 text-center bg-gray-50 py-2 rounded">
                    Household is full ({members.length}/{membersLimit} members).<br />
                </div>
            )}

            {/* Invite Dialog Modal */}
            {showInviteDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-lg font-bold">Share Household Invite</h3>
                            <button onClick={() => setShowInviteDialog(false)} className="text-gray-400 hover:text-gray-500">
                                <Icons.X />
                            </button>
                        </div>
                        <p className="text-gray-600 mb-4 text-sm">Send this link to the person you want to invite.</p>

                        <div className="flex gap-2">
                            <input
                                readOnly
                                value={inviteUrl}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono bg-gray-50"
                            />
                            <button
                                onClick={handleCopyInvite}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors border border-gray-200"
                            >
                                {copyText}
                            </button>
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setShowInviteDialog(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium text-sm"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove Member Confirmation Modal */}
            {showRemoveDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
                        <h3 className="text-lg font-bold mb-2">Remove Household Member</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to remove <span className="font-semibold">{selectedMember?.name}</span>?
                            They will no longer have access to membership benefits.
                        </p>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowRemoveDialog(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmRemoveMember}
                                disabled={isLoading}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium text-sm transition-colors"
                            >
                                {isLoading ? 'Removing...' : 'Remove Member'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
